import { HEADER, type CommandOk, type SessionView } from '@mdt/contracts';

/**
 * The PWA's only door to the server (roadmap §3.4). Commands carry an Idempotency-Key (one per logical
 * submit; a retry after a network failure reuses it), the build id and, when the person caused them,
 * X-MDT-Activity. Queries keep their ETag and send If-None-Match. Errors come back as message keys that
 * the UI renders in Thai or English; server text is never inserted as HTML.
 */

export const BUILD_ID: string = __MDT_BUILD__;

export class ApiError extends Error {
  readonly code: string;
  readonly messageKey: string;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  constructor(code: string, messageKey: string, status: number, details?: Record<string, unknown>) {
    super(`${code} ${messageKey}`);
    this.code = code;
    this.messageKey = messageKey;
    this.status = status;
    this.details = details;
  }
}

/** 'unauthenticated' (detail: message key) when a session ends under us; 'outdated' on 426. */
export const apiEvents = new EventTarget();

type Envelope = { ok?: false; error?: { code?: string; messageKey?: string; details?: Record<string, unknown> } };

async function failure(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => ({}))) as Envelope;
  const code = body.error?.code ?? (res.status === 415 ? 'VALIDATION' : 'INTERNAL');
  const key = typeof body.error?.messageKey === 'string' && /^[a-zA-Z_.]+$/.test(body.error.messageKey) ? body.error.messageKey : 'err.internal';
  return new ApiError(code, key, res.status, body.error?.details);
}

async function send(path: string, init: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(path, { credentials: 'same-origin', cache: 'no-store', ...init });
  } catch {
    throw new ApiError('NETWORK', 'err.network', 0);
  }
  if (res.ok || res.status === 304) return res;
  const e = await failure(res);
  if (res.status === 401) apiEvents.dispatchEvent(new CustomEvent('unauthenticated', { detail: e.messageKey }));
  if (res.status === 426) apiEvents.dispatchEvent(new CustomEvent('outdated'));
  throw e;
}

export type CommandOptions = {
  /** false for requests the person did not cause (background sync); they never extend the idle timer. */
  activity?: boolean;
  /** Reuse the key of an earlier attempt of the same submit. */
  idempotencyKey?: string;
};

export async function command<O>(name: string, input: unknown, opts: CommandOptions = {}): Promise<CommandOk<O>> {
  const res = await send(`/api/cmd/${encodeURIComponent(name)}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      [HEADER.idempotencyKey]: opts.idempotencyKey ?? crypto.randomUUID(),
      [HEADER.client]: BUILD_ID,
      ...(opts.activity === false ? {} : { [HEADER.activity]: '1' }),
    },
    body: JSON.stringify(input),
  });
  return (await res.json()) as CommandOk<O>;
}

const etags = new Map<string, { etag: string; data: unknown }>();

export async function query<D>(name: string, params: Record<string, string> = {}, signal?: AbortSignal): Promise<D> {
  const qs = new URLSearchParams(params).toString();
  const url = `/api/q/${encodeURIComponent(name)}${qs ? `?${qs}` : ''}`;
  const cached = etags.get(url);
  const res = await send(url, { headers: cached ? { 'if-none-match': cached.etag } : {}, ...(signal ? { signal } : {}) });
  if (res.status === 304 && cached) return cached.data as D;
  const body = (await res.json()) as { data: D };
  const etag = res.headers.get('etag');
  if (etag) etags.set(url, { etag, data: body.data });
  return body.data;
}

/** Forgets cached query results (sign-out: nothing of one person may be shown to the next). */
export const clearQueryCache = () => etags.clear();

export type SessionResult = { signedIn: true; view: SessionView } | { signedIn: false; messageKey: string };

/** GET /api/session. Background polls pass activity=false so they can never keep a session alive. */
export async function fetchSession(activity = false): Promise<SessionResult> {
  let res: Response;
  try {
    res = await fetch('/api/session', { credentials: 'same-origin', cache: 'no-store', headers: activity ? { [HEADER.activity]: '1' } : {} });
  } catch {
    throw new ApiError('NETWORK', 'err.network', 0);
  }
  if (res.status === 401) return { signedIn: false, messageKey: (await failure(res)).messageKey };
  if (!res.ok) throw await failure(res);
  return { signedIn: true, view: (await res.json()) as SessionView };
}

/** The explicit keep-alive (idle warning dialog, shared-mode activity). */
export async function keepAlive(): Promise<{ idleExpiresAt: string }> {
  const res = await send('/api/session/activity', {
    method: 'POST', headers: { 'content-type': 'application/json', [HEADER.activity]: '1' }, body: '{}',
  });
  return (await res.json()) as { idleExpiresAt: string };
}

/** POST /auth/logout: revokes the session and its draft key; the response carries Clear-Site-Data. */
export async function logoutRequest(): Promise<void> {
  try {
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: '{}' });
  } catch {
    // offline: the local wipe still runs, and the server expires the session on its own clock
  }
}

export async function draftKey(): Promise<{ keyId: string; keyB64: string }> {
  const res = await send('/api/session/draft-key', {});
  return (await res.json()) as { keyId: string; keyB64: string };
}

/**
 * Sign-in is a full-page navigation to /auth/login (Google OIDC), never a fetch. `stepUp` (ASVS 3.7.1) asks Google to
 * re-authenticate the person before a sensitive action.
 */
export function signInUrl(mode: 'standard' | 'shared', returnTo: string, stepUp = false): string {
  const qs = new URLSearchParams({ mode });
  if (stepUp) qs.set('stepUp', '1');
  if (returnTo.startsWith('/') && !returnTo.startsWith('//') && !returnTo.startsWith('/auth/') && returnTo !== '/login') qs.set('returnTo', returnTo);
  return `/auth/login?${qs.toString()}`;
}
